/**
 * NetworkAvatar — a PERMANENT, deterministic visual identity for a network.
 *
 * Every network (manual pet id or registry rn-…) gets a stable icon + gradient
 * derived ONLY from its ``networkId`` — same id always renders the same tile, no
 * randomness, no per-render drift, works retroactively for existing networks with
 * zero storage. Two networks that share a display NAME still look different
 * because the seed is the id, not the name.
 *
 * Recipe (all from one hash of the id):
 *   icon = ICONS[h % ICONS.length]         — curated lucide glyph
 *   hue  = h % 360                          — gradient stop 1
 *   hue2 = (hue + 40) % 360                 — analogous stop 2 (depth, not flat)
 *   background = 135° linear gradient, fixed S/L so the whole app stays coherent.
 *
 * Future seam: a persisted visual descriptor can carry ``customImageKey`` (an
 * uploaded image). When we add that, render the image here and fall back to this
 * generated tile — call sites don't change. Prop ``imageUrl`` is that hook today.
 *
 * One component, used everywhere a network appears (dashboard tile, header,
 * registry card) so a network is visually the SAME thing across the app.
 */
import React from 'react';
import {
  Boxes, Workflow, Cpu, GitBranch, Bot, Radar, Hexagon, Orbit, Zap, Layers,
  Compass, Network, Rocket, Sparkles, Binary, Braces,
  CircuitBoard, Cog, Database, Feather, Flame, Gem, Globe, Grid3x3, Leaf,
  Lightbulb, Magnet, Moon, Mountain, Puzzle, Shapes, Shield, Ship, Sprout,
  Star, Sun, Target, Atom, Waves,
} from 'lucide-react';

// Curated, ordered set — FROZEN order = frozen per-network assignment. Network /
// agent / build flavored, visually varied so adjacent tiles read distinct.
const ICONS = [
  Boxes, Workflow, Cpu, GitBranch, Bot, Radar, Hexagon, Orbit, Zap, Layers,
  Compass, Network, Rocket, Sparkles, Binary, Braces, CircuitBoard, Cog,
  Database, Feather, Flame, Gem, Globe, Grid3x3, Leaf, Lightbulb, Magnet,
  Moon, Mountain, Puzzle, Shapes, Shield, Ship, Sprout, Star, Sun, Target,
  Atom, Waves,
];

/** FNV-1a 32-bit — tiny, fast, well-distributed. Deterministic per string.
 *  Exported so RoomAvatar seeds from the SAME hash → visual lineage. */
export function hashString(s) {
  let h = 0x811c9dc5;
  const str = String(s || '');
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    // 32-bit FNV prime multiply, kept in uint32 range.
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

/** The curated icon set + picker, shared with RoomAvatar so a room's icon is
 *  drawn from the SAME family of glyphs as networks (consistent visual system). */
export const AVATAR_ICONS = ICONS;
export function pickIcon(hash) {
  return ICONS[hash % ICONS.length];
}

/** The network's BASE hue — the family color rooms inherit + shift within. */
export function networkHue(networkId) {
  return hashString(networkId) % 360;
}

/** Deterministic {icon, hue, hue2, gradient} for a networkId. Pure — exported
 *  so the descriptor-persist path (later) can compute the same defaults. */
export function networkVisual(networkId) {
  const h = hashString(networkId);
  const idx = h % ICONS.length;
  const hue = h % 360;
  const hue2 = (hue + 40) % 360;
  return {
    Icon: ICONS[idx],
    hue,
    hue2,
    // Fixed S/L → coherent palette; only hue varies per network.
    gradient: `linear-gradient(135deg, hsl(${hue} 62% 52%), hsl(${hue2} 62% 46%))`,
  };
}

// Regular hexagon clip-path (flat-top). SHAPE encodes PROVENANCE:
//   square  = a locally-built (manual) network
//   hexagon = a registry network (a published/deployed PACKAGE)
// Room avatars are circles — so the 3-way shape language is square/hexagon/circle.
const HEXAGON_CLIP = 'polygon(25% 5%, 75% 5%, 100% 50%, 75% 95%, 25% 95%, 0% 50%)';

export default function NetworkAvatar({
  networkId,
  name,
  size = 28,
  imageUrl = null,   // future: uploaded custom image (persisted descriptor)
  className = '',
  rounded = 'rounded-lg',
  // ``seed`` is the PORTABLE identity key. Manual networks pass nothing → derive
  // from networkId. Registry networks (listing + every tenant's deployment) pass
  // the ``visualSeed`` shipped in the package, so the icon + color are IDENTICAL
  // across all deployments regardless of each tenant's fresh local networkId.
  seed = null,
  // ``shape`` encodes provenance: 'square' (manual) | 'hexagon' (registry).
  shape = 'square',
}) {
  const label = `${name || networkId || 'network'} icon`;
  const isHex = shape === 'hexagon';
  // Hexagon has no border-radius (clip-path does the shape); square keeps rounded.
  const shapeCls = isHex ? '' : rounded;
  const shapeStyle = isHex ? { clipPath: HEXAGON_CLIP } : {};
  const boxStyle = { width: size, height: size };

  // Future custom-image path — prefer it, fall back to the generated tile.
  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt={label}
        style={{ ...boxStyle, ...shapeStyle }}
        className={`shrink-0 object-cover ${shapeCls} ${className}`}
        data-testid={`network-avatar-${networkId}`}
      />
    );
  }

  // Derive icon + color from the PORTABLE seed when given, else the network id.
  const { Icon, gradient } = networkVisual(seed || networkId);
  const iconSize = Math.round(size * 0.55);
  return (
    <span
      role="img"
      aria-label={label}
      title={name || networkId}
      data-shape={shape}
      style={{ ...boxStyle, ...shapeStyle, background: gradient }}
      className={`inline-flex shrink-0 items-center justify-center ${shapeCls} ${className}`}
      data-testid={`network-avatar-${networkId}`}
    >
      <Icon size={iconSize} strokeWidth={2} color="rgba(255,255,255,0.92)" aria-hidden />
    </span>
  );
}
